import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSnackbar } from "notistack";
import moment from "moment";
import CustomMUITable from "src/components/CustomMUITable";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import Label from "src/components/Label";
import MUICustomTabs from "src/components/GeneralComponents/MUICustomTabs";
import AddOrEditQuestionDrawer from "./AddOrEditQuestionDrawer";
import {
  cacheQuestionsFromApi,
  deleteQuestion,
  getFieldTypeLabel,
  getMeasureTypeLabel,
  getQuestionModeLabel,
  getQuestions,
  normalizeQuestion,
  QUESTION_MODES,
  resetQuestionsToDefaults,
} from "./questionStorage";
import {
  deleteTrackerQuestionApi,
  getTrackerQuestionsApi,
  seedStarterQuestionsApi,
} from "../../../DAL/trackerQuestion/trackerQuestion";

const MUTED = "#64748b";
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
  },
};

export default function TrackerQuestionsList() {
  const { enqueueSnackbar } = useSnackbar();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState(null);

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await getTrackerQuestionsApi({ limit: 500 });
      if (res?.code === 200 && Array.isArray(res?.questions)) {
        const normalized = res.questions.map(normalizeQuestion);
        setQuestions(normalized);
        cacheQuestionsFromApi(normalized);
      } else {
        setQuestions(getQuestions().map(normalizeQuestion));
      }
    } catch {
      setQuestions(getQuestions().map(normalizeQuestion));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  const counts = useMemo(() => {
    const general = questions.filter(
      (item) => item.mode === QUESTION_MODES.GENERAL
    ).length;
    const measurable = questions.filter(
      (item) => item.mode === QUESTION_MODES.MEASURABLE
    ).length;
    const coachReview = questions.filter(
      (item) => item.mode === QUESTION_MODES.COACH_REVIEW
    ).length;
    return {
      all: questions.length,
      general,
      measurable,
      coachReview,
    };
  }, [questions]);

  const filteredQuestions = useMemo(() => {
    let list = questions;
    if (tabValue === 1) {
      list = questions.filter((item) => item.mode === QUESTION_MODES.GENERAL);
    } else if (tabValue === 2) {
      list = questions.filter(
        (item) => item.mode === QUESTION_MODES.MEASURABLE
      );
    } else if (tabValue === 3) {
      list = questions.filter(
        (item) => item.mode === QUESTION_MODES.COACH_REVIEW
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => {
      const hay =
        `${item.label || ""} ${item.statement || ""} ${getFieldTypeLabel(
          item.fieldType
        )} ${getMeasureTypeLabel(item.measureType)} ${getQuestionModeLabel(
          item.mode
        )} ${(item.units || []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [questions, tabValue, search]);

  const handleDelete = async () => {
    const qId = deleteDoc?._id || deleteDoc?.id;
    if (!qId) return;

    try {
      const res = await deleteTrackerQuestionApi(qId);
      if (res?.code === 200) {
        enqueueSnackbar(res?.message || "Question deleted", {
          variant: "success",
        });
      } else {
        deleteQuestion(qId);
        enqueueSnackbar("Question deleted", { variant: "success" });
      }
    } catch {
      deleteQuestion(qId);
      enqueueSnackbar("Question deleted", { variant: "success" });
    }

    setOpenDelete(false);
    setDeleteDoc(null);
    loadQuestions();
  };

  const handleSeedStarter = async () => {
    setLoading(true);
    try {
      const res = await seedStarterQuestionsApi(true);
      if (res?.code === 200) {
        enqueueSnackbar(res?.message || "Starter questions loaded", {
          variant: "success",
        });
      } else {
        resetQuestionsToDefaults();
        enqueueSnackbar("Starter questions loaded", { variant: "success" });
      }
    } catch {
      resetQuestionsToDefaults();
      enqueueSnackbar("Starter questions loaded", { variant: "success" });
    } finally {
      loadQuestions();
    }
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "akar-icons:edit",
      handleClick: (row) => {
        setEditQuestion(row);
        setOpenForm(true);
      },
    },
    {
      label: "Delete",
      icon: "ant-design:delete-twotone",
      handleClick: (row) => {
        setDeleteDoc(row);
        setOpenDelete(true);
      },
    },
  ];

  const TABLE_HEAD = [
    {
      id: "action",
      label: "Action",
      type: "action",
      MENU_OPTIONS: "MENU_OPTIONS",
    },
    { id: "number", label: "#", type: "number" },
    {
      id: "label",
      label: "Question",
      renderData: (row) => (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {row.label || "—"}
          </Typography>
          {row.placeholder ? (
            <Typography
              sx={{ mt: 0.25, fontSize: 12, color: "text.secondary" }}
            >
              Placeholder: {row.placeholder}
            </Typography>
          ) : null}
        </Box>
      ),
    },
    {
      id: "fieldType",
      label: "Field / Measure",
      renderData: (row) =>
        row.mode === QUESTION_MODES.MEASURABLE
          ? getMeasureTypeLabel(row.measureType)
          : row.fieldType === "scale"
            ? `Scale (${row.scaleMin ?? 1}–${row.scaleMax ?? 10})`
            : getFieldTypeLabel(row.fieldType),
    },
    {
      id: "mode",
      label: "Type",
      renderData: (row) => (
        <Label
          variant="ghost"
          color={
            row.mode === QUESTION_MODES.MEASURABLE
              ? "info"
              : row.mode === QUESTION_MODES.COACH_REVIEW
                ? "warning"
                : "success"
          }
        >
          {getQuestionModeLabel(row.mode)}
        </Label>
      ),
    },
    {
      id: "units",
      label: "Units",
      renderData: (row) => {
        if (row.mode !== QUESTION_MODES.MEASURABLE) return "—";
        if (!row.units?.length) return "—";
        const defaultMark = row.defaultUnit
          ? ` (default: ${row.defaultUnit})`
          : "";
        return `${row.units.join(", ")}${defaultMark}`;
      },
    },
    {
      id: "measureDirection",
      label: "Best Outcome",
      renderData: (row) => {
        if (row.mode !== QUESTION_MODES.MEASURABLE) return "—";
        return row.measureDirection === "ascending"
          ? "Ascending"
          : "Descending";
      },
    },
    {
      id: "updatedAt",
      label: "Updated",
      renderData: (row) =>
        row.updatedAt ? moment(row.updatedAt).format("DD/MM/YYYY HH:mm") : "—",
    },
  ];

  const table = loading && !questions.length ? (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={36} />
    </Box>
  ) : (
    <CustomMUITable
      TABLE_HEAD={TABLE_HEAD}
      data={filteredQuestions}
      MENU_OPTIONS={MENU_OPTIONS}
      hide_search
      className="card-with-background tracker-module-table"
    />
  );

  const TABS_OPTIONS = [
    {
      title: `All (${counts.all})`,
      component: table,
    },
    {
      title: `General (${counts.general})`,
      component: table,
    },
    {
      title: `Measurable (${counts.measurable})`,
      component: table,
    },
    {
      title: `Coach review (${counts.coachReview})`,
      component: table,
    },
  ];

  return (
    <>
      <AddOrEditQuestionDrawer
        open={openForm}
        question={editQuestion}
        onClose={() => {
          setOpenForm(false);
          setEditQuestion(null);
        }}
        onSaved={() => {
          loadQuestions();
        }}
      />

      <CustomConfirmation
        openDelete={openDelete}
        setOpenDelete={setOpenDelete}
        title="Are you sure you want to delete this question?"
        handleAgree={handleDelete}
      />

      <div className="container-fluid tracker-module-page">
        <div className="row mobile-margin display-flex">
          <div className="col-sm-12 col-md-5">
            <h2>Tracker Questions</h2>
          </div>
          <div className="col-sm-12 col-md-7">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <TextField
                size="small"
                label="Search"
                placeholder="Question, type, units…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  ...fieldSx,
                  width: { xs: "100%", sm: 240 },
                  flexShrink: 0,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: MUTED }} />
                    </InputAdornment>
                  ),
                }}
              />
              {questions.length === 0 && (
                <button
                  className="small-contained-button"
                  disabled={loading}
                  onClick={handleSeedStarter}
                >
                  {loading ? "Loading…" : "Load Starter Questions"}
                </button>
              )}
              <button
                className="small-contained-button"
                onClick={() => {
                  setEditQuestion(null);
                  setOpenForm(true);
                }}
              >
                Add Question
              </button>
            </Box>
          </div>

          <div className="col-12" style={{ marginTop: 16 }}>
            <MUICustomTabs
              data={TABS_OPTIONS}
              value={tabValue}
              handleChange={(event, newValue) => setTabValue(newValue)}
            />
          </div>
        </div>
      </div>
    </>
  );
}
